import * as React from 'react'
import { FC } from 'react'
import { useFormContext } from 'react-hook-form'
import { compact, isEmpty } from 'lodash'
import useMe from '@hooks/useMe'
import MultilineFieldNew from '@templates/MultilineFieldNew'
import Field from '@templates/Field'
import { Option } from '@organs/Select'
import useJobDataForFields, { JobData } from '@hooks/useJobDataForFields'
import { Filial } from '@models/filial'
import { Division } from '@models/division'
import useDivisions from '@hooks/useDivisions'
import { createInitFilter } from '@utils/filters'
import usePositions from '@hooks/usePositions'
import { selectSearchField } from '@templates/AddNewItemModal/templates'
import { REQUIRED_VALIDATION_RULE } from '@const/validation'

const FIELDS_NAME = 'subdivisionJobPositionRels'

export const getFieldName = (prefix: string, index: number) =>
  `${FIELDS_NAME}.${index}.${prefix}`

const FieldsSrcRender: FC<{ index: number; filial: JobData | null }> = ({
  index,
  filial,
}) => {
  const {
    divisionCuratorWithOneDivision,
    filialCuratorWithOneDivision,
    companyBranch,
  } = useMe()

  const methods = useFormContext()
  const { getValues, setValue } = methods

  let selectedFilial: Filial | undefined = methods.watch('filial')
  if (filialCuratorWithOneDivision && !filial) {
    selectedFilial = filialCuratorWithOneDivision
  }

  let selectedDivision: Division | undefined = methods.watch(
    getFieldName('division', index)
  )

  if (divisionCuratorWithOneDivision && !filial) {
    selectedFilial = companyBranch
    selectedDivision = divisionCuratorWithOneDivision
  }

  if (!divisionCuratorWithOneDivision && !selectedFilial && !filial) {
    selectedFilial = companyBranch
  }

  const divisionsResponse = useDivisions({
    skip: !selectedFilial,
    ...(selectedFilial?.id
      ? { initFilters: createInitFilter('companyBranch', selectedFilial?.id) }
      : {}),
  })

  const positionsResponse = usePositions({
    skip: !selectedDivision,
    ...(selectedDivision?.id
      ? {
          initFilters: createInitFilter('subdivision', selectedDivision?.id),
        }
      : {}),
  })

  const fieldsSrc = compact([
    !divisionCuratorWithOneDivision &&
      selectSearchField({
        label: 'Подразделение',
        name: 'division',
        isDisabled: !selectedFilial,
        options: divisionsResponse.data?.items || [],
        isLoading: divisionsResponse.isLoading,
        setFilters: divisionsResponse.setFilters,
        setPage: divisionsResponse.setPage,
        length: divisionsResponse.data?.length || 0,
        isClearable: false,
        componentProps: {
          onChange: () => {
            setValue(getFieldName('position', index), null)
          },
        },
      }),
    selectSearchField({
      label: 'Должность',
      name: 'position',
      isDisabled: isEmpty(selectedDivision),
      options: positionsResponse.data?.items || [],
      isLoading: positionsResponse.isLoading,
      setFilters: positionsResponse.setFilters,
      setPage: positionsResponse.setPage,
      length: positionsResponse.data?.length || 0,
      isRequired: !isEmpty(selectedDivision),
      rules: {
        ...(!isEmpty(selectedDivision)
          ? REQUIRED_VALIDATION_RULE
          : { required: undefined }),
      },
      isClearable: false,
    }),
  ])

  const selectedMultilineValues =
    (getValues()[FIELDS_NAME] as Option[])?.map((option) => option?.value) || []

  return (
    <>
      {fieldsSrc.map((item) => (
        <Field
          key={item.name}
          {...item}
          componentProps={{
            ...item.componentProps,
            selectedMultilineValues,
          }}
          name={
            fieldsSrc.length === 1
              ? `${FIELDS_NAME}.${index}`
              : `${FIELDS_NAME}.${index}.${item.name}`
          }
        />
      ))}
    </>
  )
}

const DivisionPositionMultilineFields: FC = () => {
  const { divisionCuratorWithOneDivision } = useMe()
  const methods = useFormContext()
  const { filial } = useJobDataForFields(methods)

  return (
    <MultilineFieldNew
      fieldsName={FIELDS_NAME}
      amendValue={
        divisionCuratorWithOneDivision
          ? {}
          : {
              division: {},
              position: {},
            }
      }
      addLabel={
        divisionCuratorWithOneDivision
          ? 'добавить должность'
          : 'добавить подразделение и должность'
      }
      fieldId="position-division"
      FieldsSrcRender={({ index }) => (
        <FieldsSrcRender filial={filial} index={index} />
      )}
    />
  )
}

export default DivisionPositionMultilineFields
